/**
 * Integration Repository
 * Responsabilidade: Operações de CRUD para integrações no Supabase.
 *
 * Princípios: Repository Pattern, Single Responsibility, DRY
 */

import { supabase } from '../../../../lib/supabase';
import { preferPermanentAvatarUrl } from '../../../../utils/failedImageCache';

// ============================================================================
// TYPES
// ============================================================================

export interface IntegrationConfig {
    instance_name: string;
    owner_jid?: string;
    display_number?: string;
    profile_pic?: string;
}

export interface IntegrationData {
    id: string;
    config: IntegrationConfig;
}

// ============================================================================
// FUNCTIONS
// ============================================================================

/**
 * Mantém whatsapp_instances alinhado com integrations.
 * O Canais usa ambas as fontes — polling só atualizava integrations e deixava a UI “Desconectado”.
 */
async function syncWhatsappInstanceStatus(
    integrationId: string,
    instanceName: string,
    status: 'connected' | 'disconnected',
    extras?: {
        phoneNumber?: string;
        profilePictureUrl?: string;
    }
): Promise<void> {
    const { data: integration } = await supabase
        .from('integrations')
        .select('organization_id')
        .eq('id', integrationId)
        .maybeSingle();

    if (!integration?.organization_id) return;

    const now = new Date().toISOString();
    const payload: Record<string, unknown> = {
        integration_id: integrationId,
        organization_id: integration.organization_id,
        instance_name: instanceName,
        status,
        updated_at: now,
    };

    if (status === 'connected') {
        payload.last_connected_at = now;
        payload.disconnect_reason = null;
    } else {
        payload.last_disconnected_at = now;
    }

    if (extras?.phoneNumber) payload.phone_number = extras.phoneNumber;

    // Lê foto atual para não sobrescrever R2 com CDN temporária do polling
    let nextPicture = extras?.profilePictureUrl;
    if (nextPicture) {
        const { data: existingInstance } = await supabase
            .from('whatsapp_instances')
            .select('profile_picture_url')
            .eq('instance_name', instanceName)
            .maybeSingle();
        nextPicture = preferPermanentAvatarUrl(
            existingInstance?.profile_picture_url,
            nextPicture,
        ) || undefined;
        if (nextPicture) payload.profile_picture_url = nextPicture;
    }

    const { error } = await supabase
        .from('whatsapp_instances')
        .upsert(payload, { onConflict: 'organization_id,instance_name' });

    if (error) {
        const { error: updateError } = await supabase
            .from('whatsapp_instances')
            .update({
                status,
                updated_at: now,
                ...(status === 'connected'
                    ? { last_connected_at: now, disconnect_reason: null }
                    : { last_disconnected_at: now }),
                ...(extras?.phoneNumber ? { phone_number: extras.phoneNumber } : {}),
                ...(nextPicture ? { profile_picture_url: nextPicture } : {}),
            })
            .eq('instance_name', instanceName);

        if (updateError) {
            console.warn('[IntegrationRepo] Falha ao sincronizar whatsapp_instances:', updateError);
        }
    }
}

/**
 * Cria pré-registro de integração no banco antes de conectar na Uazapi.
 * Garante que quando o webhook chegar, ele encontrará o registro.
 */
export async function createIntegrationPreRegister(
    instanceName: string,
    displayName: string
): Promise<string | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: orgMember } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

    const organizationId = orgMember?.organization_id;

    const { data, error } = await supabase.from('integrations').insert({
        user_id: user.id,
        organization_id: organizationId,
        type: 'whatsapp',
        name: displayName,
        status: 'disconnected',
        config: {
            instance_name: instanceName,
        }
    }).select('id').single();

    if (error) {
        console.error('[IntegrationRepo] Erro ao pré-registrar:', error);
        return null;
    }
    return data.id;
}

/**
 * Deleta integração pelo nome da instância.
 */
export async function deleteIntegrationByInstance(instanceName: string): Promise<void> {
    await supabase.from('integrations').delete().eq('config->>instance_name', instanceName);
}

/**
 * Atualiza integração após conexão bem-sucedida.
 * Implementa "reconexão inteligente" - reutiliza integração antiga se existir.
 */
export async function updateIntegrationAfterConnect(
    integrationId: string,
    instanceName: string,
    instanceData: any,
    originalName: string
): Promise<void> {
    // Formata o número
    const rawNumber = instanceData.number || instanceData.owner || '';
    const cleanNumber = rawNumber.replace('@s.whatsapp.net', '').replace(/\D/g, '');

    // Prioridade do Nome
    const displayName = instanceData.name
        || instanceData.profileName
        || instanceData.pushName
        || originalName
        || cleanNumber
        || instanceName;

    console.log('[IntegrationRepo] Atualizando conexão:', { displayName, cleanNumber });

    // RECONEXÃO INTELIGENTE: Verificar se já existe integração com este número
    if (cleanNumber) {
        const { data: existingIntegration } = await supabase
            .from('integrations')
            .select('id, config')
            .eq('type', 'whatsapp')
            .or(`config->>display_number.eq.${cleanNumber},config->>owner_jid.ilike.%${cleanNumber}%`)
            .neq('id', integrationId)
            .limit(1)
            .maybeSingle();

        if (existingIntegration) {
            console.log(`[IntegrationRepo] 🔄 Reutilizando integração antiga: ${existingIntegration.id}`);

            const keptPic = preferPermanentAvatarUrl(
                (existingIntegration.config as IntegrationConfig | null)?.profile_pic,
                instanceData.profilePictureUrl,
            );

            // Atualiza a integração ANTIGA
            await supabase.from('integrations').update({
                name: displayName,
                status: 'connected',
                config: {
                    ...existingIntegration.config,
                    instance_name: instanceName,
                    owner_jid: rawNumber,
                    display_number: cleanNumber,
                    ...(keptPic ? { profile_pic: keptPic } : {}),
                },
                updated_at: new Date().toISOString()
            }).eq('id', existingIntegration.id);

            await syncWhatsappInstanceStatus(existingIntegration.id, instanceName, 'connected', {
                phoneNumber: cleanNumber || undefined,
                profilePictureUrl: keptPic || undefined,
            });

            // Remove a integração NOVA (temporária)
            await supabase.from('integrations').delete().eq('id', integrationId);
            return;
        }
    }

    const keptPic = preferPermanentAvatarUrl(null, instanceData.profilePictureUrl);

    // Fluxo normal: atualiza a nova integração
    await supabase.from('integrations').update({
        name: displayName,
        status: 'connected',
        config: {
            instance_name: instanceName,
            owner_jid: rawNumber,
            display_number: cleanNumber,
            ...(keptPic ? { profile_pic: keptPic } : {}),
        }
    }).eq('id', integrationId);

    await syncWhatsappInstanceStatus(integrationId, instanceName, 'connected', {
        phoneNumber: cleanNumber || undefined,
        profilePictureUrl: keptPic || undefined,
    });
}

/**
 * Atualiza status simples de uma integração.
 * MERGE de config — preserva campos existentes como instance_name.
 * Também sincroniza whatsapp_instances (fonte usada na listagem de Canais).
 */
export async function updateIntegrationStatus(
    integrationId: string,
    status: 'connected' | 'disconnected',
    config?: Partial<IntegrationConfig>
): Promise<void> {
    const updateData: Record<string, unknown> = { status, updated_at: new Date().toISOString() };

    const { data: existing } = await supabase
        .from('integrations')
        .select('config')
        .eq('id', integrationId)
        .maybeSingle();

    const incomingPic = config?.profile_pic;
    const keptPic = preferPermanentAvatarUrl(
        (existing?.config as IntegrationConfig | null)?.profile_pic,
        incomingPic,
    );
    const mergedConfig = {
        ...(existing?.config || {}),
        ...config,
        ...(incomingPic !== undefined ? { profile_pic: keptPic || undefined } : {}),
    };

    if (config) {
        updateData.config = mergedConfig;
    }

    await supabase.from('integrations').update(updateData).eq('id', integrationId);

    const instanceName = mergedConfig.instance_name as string | undefined;
    if (instanceName) {
        const phone = (mergedConfig.display_number || mergedConfig.owner_jid || '')
            .toString()
            .replace('@s.whatsapp.net', '')
            .replace(/\D/g, '');
        await syncWhatsappInstanceStatus(integrationId, instanceName, status, {
            phoneNumber: phone || undefined,
            profilePictureUrl: mergedConfig.profile_pic || undefined,
        });
    }
}
